import "@/App.css";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider, useAuth, api } from "@/contexts/AuthContext";
import { SUPPORTED_LOCALES } from "@/i18n";
import { Toaster } from "@/components/ui/sonner";
import DemoModeBanner from "@/components/DemoModeBanner";
import { trackPageview } from "@/utils/analytics";
import AuthCallback from "@/pages/AuthCallback";
import AuthGoogleCallback from "@/pages/AuthGoogleCallback";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import LinkAccount from "@/pages/LinkAccount";
import MagicLinkPage from "@/pages/MagicLinkPage";
import ProfileSetup from "@/pages/ProfileSetup";
import Discover from "@/pages/Discover";
import Matches from "@/pages/Matches";
import Chat from "@/pages/Chat";
import MatchProfileView from "@/pages/MatchProfileView";
import Settings from "@/pages/Settings";
import Profile from "@/pages/Profile";
import ProfileV2 from "@/pages/ProfileV2";
import AIProfileCoach from "@/pages/AIProfileCoach";
import Support from "@/pages/Support";
import CookiePolicy from "@/pages/CookiePolicy";
import CommunityGuidelines from "@/pages/CommunityGuidelines";
import {
  SafetyHub, SafetyEmergency, SafetyReport, SafetyRedFlags, SafetyTips,
  SafetyShareDate, SafetyHealthyDating, SafetyVerification, SafetyCredibility,
} from "@/pages/Safety";
import AISafetyChat from "@/pages/AISafetyChat";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import Press from "@/pages/Press";
import Articles from "@/pages/Articles";
import Article from "@/pages/Article";
import Subscription from "@/pages/Subscription";
import Store from "@/pages/Store";
import BoostPage from "@/pages/BoostPage";
import SafeMeet from "@/pages/SafeMeet";
import SafeMeetNew from "@/pages/SafeMeetNew";
import ProfileOptimizer from "@/pages/ProfileOptimizer";
import Verification from "@/pages/Verification";
import LikesReceived from "@/pages/LikesReceived";
import Messages from "@/pages/Messages";
import MeetingPrefs from "@/pages/MeetingPrefs";
import TravelMode from "@/pages/TravelMode";
import PrivacyVisibility from "@/pages/PrivacyVisibility";
import DeactivateAccount from "@/pages/DeactivateAccount";
import DataPrivacy from "@/pages/DataPrivacy";
import LoginSecurity from "@/pages/LoginSecurity";
import AdminSupport from "@/pages/AdminSupport";
import AdminFinancials from "@/pages/AdminFinancials";
import AdminRatios from "@/pages/AdminRatios";
import AdminModeration from "@/pages/AdminModeration";
import AdminPushLog from "@/pages/AdminPushLog";
import AdminFraud from "@/pages/AdminFraud";
import AdminUsers from "@/pages/AdminUsers";
import AdminUserDetail from "@/pages/AdminUserDetail";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminVerifySignals from "@/pages/AdminVerifySignals";
import AdminTestimonials from "@/pages/AdminTestimonials";
import AdminAbuse from "@/pages/AdminAbuse";
import ShareStory from "@/pages/ShareStory";
import NotFound from "@/pages/NotFound";
import AdminScoring from "@/pages/AdminScoring";
import AdminGrants from "@/pages/AdminGrants";
import ScoringDetail from "@/pages/ScoringDetail";
import Credibility from "@/pages/Credibility";
import Referrals from "@/pages/Referrals";
import ProtectedRoute from "@/components/ProtectedRoute";
import BottomNav from "@/components/BottomNav";
import PWAInstallBanner from "@/components/PWAInstallBanner";

const NO_NAV = ['/auth', '/', '/profile/setup', '/privacy', '/terms', '/subscription', '/safe-meet', '/safe-meet/new', '/profile-score', '/verify', '/meeting-prefs', '/credibility', '/referrals', '/admin', '/admin/support', '/admin/moderation', '/admin/users', '/admin/analytics', '/admin/financials', '/admin/ratios', '/admin/scoring', '/admin/push-log', '/admin/verify-signals', '/scoring-detail'];

// Sprint C flipped to default (Feb 2026). Old profile is now behind `?v1=1`
// for quick rollback; delete this + `Profile.jsx` once V2 is stable.
function ProfileGate() {
  // Web UX Parity §5 (Jun 2026): new Hinge-inspired lean nav Profile is default.
  // ProfileV2 (Feb 2026 Sprint C dashboard) kept behind `?v2=1` as fallback.
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  return params.get('v2') === '1' ? <ProfileV2 /> : <Profile />;
}

function useSyncLanguageFromSettings(user) {
  const { i18n } = useTranslation();
  useEffect(() => {
    if (!user) return;
    let cancel = false;
    api.get('/settings').then((r) => {
      if (cancel) return;
      const lang = r?.data?.language;
      if (lang && SUPPORTED_LOCALES.includes(lang) && i18n.language !== lang) {
        i18n.changeLanguage(lang);
      }
    }).catch(() => {});
    return () => { cancel = true; };
  }, [user, i18n]);
}

function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();
  useSyncLanguageFromSettings(user);

  // Fire pageview on every route change (skips admin internally on the server).
  useEffect(() => {
    if (!location.pathname.startsWith('/admin')) {
      trackPageview(location.pathname);
    }
  }, [location.pathname]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  const showNav = user && !NO_NAV.includes(location.pathname) && !location.pathname.startsWith('/chat/') && !location.pathname.startsWith('/admin/users/');

  return (
    <>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/auth/google/callback" element={<AuthGoogleCallback />} />
        <Route path="/link-account" element={<ProtectedRoute><LinkAccount /></ProtectedRoute>} />
        <Route path="/magic" element={<MagicLinkPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/cookies" element={<CookiePolicy />} />
        <Route path="/guidelines" element={<CommunityGuidelines />} />
        <Route path="/press" element={<Press />} />
        <Route path="/articles" element={<Articles />} />
        <Route path="/articles/:slug" element={<Article />} />
        <Route path="/profile/setup" element={<ProtectedRoute><ProfileSetup /></ProtectedRoute>} />
        <Route path="/discover" element={<ProtectedRoute><Discover /></ProtectedRoute>} />
        <Route path="/matches" element={<ProtectedRoute><Matches /></ProtectedRoute>} />
        <Route path="/chat/:matchId" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
        <Route path="/user/:userId" element={<ProtectedRoute><MatchProfileView /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfileGate /></ProtectedRoute>} />
        <Route path="/ai-profile-coach" element={<ProtectedRoute><AIProfileCoach /></ProtectedRoute>} />
        <Route path="/subscription" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />
        <Route path="/store" element={<ProtectedRoute><Store /></ProtectedRoute>} />
        <Route path="/boost" element={<ProtectedRoute><BoostPage /></ProtectedRoute>} />
        <Route path="/safe-meet" element={<ProtectedRoute><SafeMeet /></ProtectedRoute>} />
        <Route path="/safe-meet/new" element={<ProtectedRoute><SafeMeetNew /></ProtectedRoute>} />
        {/* Support + Safety hub — Sprint D (Feb 2026). Public so signed-out users can reach them from footer/emails. */}
        <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
        <Route path="/safety" element={<SafetyHub />} />
        <Route path="/safety/emergency" element={<SafetyEmergency />} />
        <Route path="/safety/report" element={<SafetyReport />} />
        <Route path="/safety/red-flags" element={<SafetyRedFlags />} />
        <Route path="/safety/tips" element={<SafetyTips />} />
        <Route path="/safety/share-date" element={<SafetyShareDate />} />
        <Route path="/safety/healthy-dating" element={<SafetyHealthyDating />} />
        <Route path="/safety/verification" element={<SafetyVerification />} />
        <Route path="/safety/credibility" element={<SafetyCredibility />} />
        <Route path="/safety/ai-assistant" element={<ProtectedRoute><AISafetyChat mode="safety" /></ProtectedRoute>} />
        <Route path="/ai-coach" element={<ProtectedRoute><AISafetyChat mode="dm" /></ProtectedRoute>} />
        <Route path="/profile-score" element={<ProtectedRoute><ProfileOptimizer /></ProtectedRoute>} />
        <Route path="/verify" element={<ProtectedRoute><Verification /></ProtectedRoute>} />
        <Route path="/likes" element={<ProtectedRoute><LikesReceived /></ProtectedRoute>} />
        <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
        <Route path="/meeting-prefs" element={<ProtectedRoute><MeetingPrefs /></ProtectedRoute>} />
        <Route path="/travel" element={<ProtectedRoute><TravelMode /></ProtectedRoute>} />
        <Route path="/settings/privacy-visibility" element={<ProtectedRoute><PrivacyVisibility /></ProtectedRoute>} />
        <Route path="/settings/deactivate" element={<ProtectedRoute><DeactivateAccount /></ProtectedRoute>} />
        <Route path="/settings/data-privacy" element={<ProtectedRoute><DataPrivacy /></ProtectedRoute>} />
        <Route path="/settings/login-security" element={<ProtectedRoute><LoginSecurity /></ProtectedRoute>} />
        <Route path="/credibility" element={<ProtectedRoute><Credibility /></ProtectedRoute>} />
        <Route path="/referrals" element={<ProtectedRoute><Referrals /></ProtectedRoute>} />
        <Route path="/admin/support" element={<ProtectedRoute><AdminSupport /></ProtectedRoute>} />
        <Route path="/admin/moderation" element={<ProtectedRoute><AdminModeration /></ProtectedRoute>} />
        <Route path="/admin/fraud" element={<ProtectedRoute><AdminFraud /></ProtectedRoute>} />
        <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalytics /></ProtectedRoute>} />
        <Route path="/admin/verify-signals" element={<ProtectedRoute><AdminVerifySignals /></ProtectedRoute>} />
        <Route path="/admin/testimonials" element={<ProtectedRoute><AdminTestimonials /></ProtectedRoute>} />
        <Route path="/admin/abuse" element={<ProtectedRoute><AdminAbuse /></ProtectedRoute>} />
        <Route path="/share-story" element={<ProtectedRoute><ShareStory /></ProtectedRoute>} />
        <Route path="/admin/financials" element={<ProtectedRoute><AdminFinancials /></ProtectedRoute>} />
        <Route path="/admin/ratios" element={<ProtectedRoute><AdminRatios /></ProtectedRoute>} />
        <Route path="/admin/scoring" element={<ProtectedRoute><AdminScoring /></ProtectedRoute>} />
        <Route path="/admin/push-log" element={<ProtectedRoute><AdminPushLog /></ProtectedRoute>} />
        <Route path="/admin/grants" element={<ProtectedRoute><AdminGrants /></ProtectedRoute>} />
        <Route path="/scoring-detail" element={<ProtectedRoute><ScoringDetail /></ProtectedRoute>} />
        <Route path="/admin/users/:userId" element={<ProtectedRoute><AdminUserDetail /></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      {showNav && <BottomNav />}
      {user && <PWAInstallBanner />}
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <DemoModeBanner />
        <AppRouter />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
